import React from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './components/Toast';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import AgentLayout from './components/AgentLayout';

import AdminLogin from './pages/AdminLogin';
import Dashboard from './pages/Dashboard';
import Listings from './pages/Listings';
import ListingDetail from './pages/ListingDetail';
import Leads from './pages/Leads';
import Legal from './pages/Legal';
import LegalServiceDetail from './pages/LegalServiceDetail';
import ServiceOrders from './pages/ServiceOrders';
import ServiceOrderDetail from './pages/ServiceOrderDetail';
import ServiceRequestDetail from './pages/ServiceRequestDetail';
import ApplicationOrders from './pages/ApplicationOrders';
import ApplicationDetail from './pages/ApplicationDetail';
import ServiceCategories from './pages/ServiceCategories';
import Maintenance from './pages/Maintenance';
import Agents from './pages/Agents';
import AgentDetail from './pages/AgentDetail';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import Revenue from './pages/Revenue';
import Subscriptions from './pages/Subscriptions';
import Settings from './pages/Settings';
import PolicyPages from './pages/PolicyPages';

import AgentLogin from './pages/AgentLogin';
import AgentRegister from './pages/AgentRegister';
import ForgotPassword from './pages/ForgotPassword';
import AgentDashboard from './pages/AgentDashboard';
import AgentListings from './pages/AgentListings';
import AgentAddListing from './pages/AgentAddListing';
import AgentInquiries from './pages/AgentInquiries';
import AgentMaintenance from './pages/AgentMaintenance';
import AgentSubscription from './pages/AgentSubscription';
import AgentSettings from './pages/AgentSettings';

import AppLanding from './pages/AppLanding';
import Faq from './pages/Faq';
import PageRoute from './components/PageRoute';

const App: React.FC = () => (
  <AuthProvider>
    <ToastProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/admin/login"    element={<AdminLogin />} />
          <Route path="/agent/login"    element={<AgentLogin />} />
          <Route path="/agent/register" element={<AgentRegister />} />
          <Route path="/agent/forgot-password" element={<ForgotPassword portal="agent" />} />

          {/* Public pages — linked from the App Store / Play Store listings. */}
          <Route path="/"               element={<AppLanding />} />
          <Route path="/app"            element={<Navigate to="/" replace />} />
          <Route path="/faq"            element={<Faq />} />

          <Route
            path="/admin"
            element={
              <ProtectedRoute requireRole="admin" loginPath="/admin/login">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index               element={<Dashboard />} />
            <Route path="listings"     element={<Listings />} />
            {/* Same wizard as the agent panel; admin mode adds agent assignment. */}
            <Route path="listings/new" element={<AgentAddListing />} />
            <Route path="listings/:id" element={<ListingDetail />} />
            <Route path="leads"        element={<Leads />} />
            <Route path="revenue"      element={<Revenue />} />
            <Route path="legal"        element={<Legal />} />
            <Route path="legal/:id"    element={<LegalServiceDetail />} />
            {/* Both routes render ServiceOrders locked to one kind — legal on
                Legal Services Request, maintenance on Service Order. Distinct keys
                force a remount so filter state doesn't leak between them. */}
            <Route path="orders"       element={<ServiceOrders key="orders" lockedKind="legal" />} />
            <Route path="orders/:id"   element={<ServiceOrderDetail />} />
            <Route
              path="service-requests"
              element={<ServiceOrders key="service-requests" lockedKind="maintenance" />}
            />
            <Route path="service-requests/:id" element={<ServiceRequestDetail />} />
            <Route path="applications" element={<ApplicationOrders />} />
            <Route path="applications/:id" element={<ApplicationDetail />} />
            <Route path="service-categories" element={<ServiceCategories />} />
            <Route path="maintenance"  element={<Maintenance />} />
            <Route path="agents"       element={<Agents />} />
            <Route path="agents/:id"   element={<AgentDetail />} />
            <Route path="users"        element={<Users />} />
            <Route path="users/:id"    element={<UserDetail />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            {/* Stripe keys moved into Settings; keep old links working. */}
            <Route path="stripe-keys"  element={<Navigate to="/admin/settings?tab=stripe" replace />} />
            {/* Terms and Privacy each have their own sidebar item and address. */}
            <Route path="policies"     element={<Navigate to="/admin/policies/terms-of-service" replace />} />
            <Route path="policies/:slug" element={<PolicyPages />} />
            <Route path="settings"     element={<Settings />} />
          </Route>

          <Route
            path="/agent"
            element={
              <ProtectedRoute requireRole="agent" loginPath="/agent/login">
                <AgentLayout />
              </ProtectedRoute>
            }
          >
            <Route index               element={<AgentDashboard />} />
            <Route path="listings"     element={<AgentListings />} />
            <Route path="listings/new" element={<AgentAddListing />} />
            {/* Reuses the admin listing detail page, pointed back at /agent. */}
            <Route path="listings/:id" element={<ListingDetail basePath="/agent" />} />
            <Route path="listings/:id/edit" element={<AgentAddListing />} />
            <Route path="inquiries"    element={<AgentInquiries />} />
            <Route path="maintenance"  element={<AgentMaintenance />} />
            <Route path="subscription" element={<AgentSubscription />} />
            <Route path="settings"     element={<AgentSettings />} />
          </Route>

          {/* Privacy Policy / Terms live at admin-editable addresses; anything
              else goes to the admin login. */}
          <Route path="*" element={<PageRoute />} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  </AuthProvider>
);

export default App;
